import React from 'react'
import { Modal as BootstrapModal } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { Button } from '../../components/Button/Button'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { IntegrationSavedModalBody } from '../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { IntegrationFieldReview } from '../../components/Integrations/IntegrationFieldReview/IntegrationFieldReview'
import { DataProcessingView } from '../UpdateExternalContact/InitCreateOrUpdateExternalContact/DataProcessing/DataProcessingView'
import { getIntegrationDisplayName } from '../../common/integrations'
import { UseUpdateIntegrationObject } from './useUpdateIntegrationObject'
import { integrationObjectRoutingKey } from './utils'
import './UpdateIntegrationObjectModal.scss'

interface UpdateIntegrationObjectModalProps {
  updateIntegrationObject: UseUpdateIntegrationObject
  onHide: () => void
}

const REVIEW_PHASES: ReadonlyArray<UseUpdateIntegrationObject['phase']> = ['generating', 'review', 'saving', 'success', 'error']

export const UpdateIntegrationObjectModal: React.FC<UpdateIntegrationObjectModalProps> = ({ updateIntegrationObject, onHide }) => {
  const { object, mode } = updateIntegrationObject
  const show = REVIEW_PHASES.includes(updateIntegrationObject.phase)

  if (!object) {
    return null
  }

  const serviceId = object.serviceId
  const integrationDisplayName = getIntegrationDisplayName(serviceId)
  const isCreate = mode === 'create'

  const handleHide = () => {
    updateIntegrationObject.reset()
    onHide()
  }

  const handleRestart = () => {
    updateIntegrationObject.restartSelection()
  }

  const renderReview = () => (
    <div className='update-integration-object-review d-flex flex-column h-100'>
      <div className='update-integration-object-review__meta paragraph-small mb-3'>
        <span>
          <span className='update-integration-object-review__label'>Updating:</span> {object.displayName}
          {updateIntegrationObject.contactName ? ` — ${updateIntegrationObject.contactName}` : ''}
        </span>
        {updateIntegrationObject.contactName ? (
          <span>
            <span className='update-integration-object-review__label'>Contact:</span> {updateIntegrationObject.contactName}
          </span>
        ) : null}
        {updateIntegrationObject.generatedAt ? (
          <span>
            <span className='update-integration-object-review__label'>Created:</span> {new Date(updateIntegrationObject.generatedAt).toLocaleDateString()}
          </span>
        ) : null}
      </div>

      <IntegrationFieldReview
        serviceId={serviceId}
        groupLabel={isCreate ? `New ${object.displayName}` : object.displayName}
        fields={updateIntegrationObject.fields}
        values={updateIntegrationObject.values}
        originalValues={updateIntegrationObject.originalValues}
        selectedKeys={updateIntegrationObject.acceptedKeys}
        onToggleField={updateIntegrationObject.toggleAccept}
        onToggleAll={updateIntegrationObject.setAllAccepted}
        onEditValue={updateIntegrationObject.editValue}
        mode={mode}
        currentValues={updateIntegrationObject.currentValues}
        buildEntityRoutingKey={integrationObjectRoutingKey}
      />
    </div>
  )

  const renderSuccess = () => {
    if (!updateIntegrationObject.syncStatus) {
      return null
    }
    return (
      <div className='update-integration-object-panel'>
        <IntegrationSavedModalBody
          serviceId={serviceId}
          syncStatuses={[updateIntegrationObject.syncStatus]}
          onHide={handleHide}
          showSidebar={false}
          compact={false}
          fullHeight
          showCloseButton={false}
        />
      </div>
    )
  }

  const renderError = () => (
    <div className='d-flex flex-column align-items-center justify-content-center py-5 gap-3 text-center'>
      <i className='bi bi-exclamation-triangle-fill update-integration-object-error-icon' />
      <span className='paragraph-medium'>{updateIntegrationObject.errorMessage}</span>
    </div>
  )

  const isCardPhase = updateIntegrationObject.phase !== 'review' && updateIntegrationObject.phase !== 'saving'
  const bodyClassName = isCardPhase ? 'd-flex flex-column' : 'mt-3 d-flex flex-column'

  const getPhaseView = (): { body: React.ReactNode; footer: React.ReactNode; showFooterActions: boolean; footerClassName: string } => {
    const defaultFooterClassName = 'd-flex justify-content-end align-items-center'
    switch (updateIntegrationObject.phase) {
      case 'review':
      case 'saving':
        return {
          body: renderReview(),
          showFooterActions: true,
          footerClassName: 'd-flex justify-content-between align-items-center',
          footer: (
            <div className='update-integration-object-rerun paragraph-small zocks-text-gray-600'>
              <div>If the target system has been updated since, please rerun results</div>
              <div className='d-flex align-items-center zocks-gap-0.25'>
                {updateIntegrationObject.generatedAt ? <span>Last generated on {new Date(updateIntegrationObject.generatedAt).toLocaleString()}</span> : null}
                <Button
                  className='update-integration-object-rerun__btn p-0 border-0 bg-transparent ms-1'
                  onClick={updateIntegrationObject.reviewUpdates}
                  disabled={updateIntegrationObject.phase === 'saving'}
                >
                  Rerun
                </Button>
              </div>
            </div>
          ),
        }
      case 'success':
        return {
          body: renderSuccess(),
          showFooterActions: false,
          footerClassName: defaultFooterClassName,
          footer: (
            <div className='d-flex gap-3'>
              <Button variant='primaryGhost' onClick={handleRestart}>
                + Update another object
              </Button>
              <Button variant='primary' onClick={handleHide}>
                Close
              </Button>
            </div>
          ),
        }
      case 'error':
        return {
          body: renderError(),
          showFooterActions: false,
          footerClassName: defaultFooterClassName,
          footer: (
            <div className='d-flex gap-3'>
              <Button variant='secondary' onClick={handleHide}>
                Cancel
              </Button>
              <Button variant='primary' onClick={updateIntegrationObject.retry}>
                Try again
              </Button>
            </div>
          ),
        }
      case 'generating':
      default:
        return {
          body: (
            <div className='update-integration-object-panel'>
              <DataProcessingView serviceId={serviceId} title='We are processing your data' subtitle='Please wait until it is completed' />
            </div>
          ),
          footer: null,
          showFooterActions: false,
          footerClassName: defaultFooterClassName,
        }
    }
  }

  const { body, footer, showFooterActions, footerClassName } = getPhaseView()

  return (
    <Modal
      dialogClassName='modal-size-75'
      className='modal-xl'
      scrollable={true}
      show={show}
      onHide={handleHide}
      header={
        <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-start'>
          <IntegrationIcon serviceId={serviceId} className='me-2' width='2rem' height='2rem' />
          Save {object.displayName} to {integrationDisplayName}
        </BootstrapModal.Title>
      }
      body={body}
      footer={footer}
      footerClassName={footerClassName}
      showFooterActions={showFooterActions}
      footerPrimaryActionTitle={`${isCreate ? 'Create record' : 'Update all'} (${updateIntegrationObject.acceptedCount})`}
      footerPrimaryActionDisabled={updateIntegrationObject.acceptedCount === 0 || updateIntegrationObject.phase === 'saving'}
      footerPrimaryActionLoading={updateIntegrationObject.phase === 'saving'}
      footerSecondaryActionTitle='Back to start'
      footerSecondaryActionDisabled={updateIntegrationObject.phase === 'saving'}
      onFooterPrimaryAction={updateIntegrationObject.submit}
      onFooterSecondaryAction={handleRestart}
      centered={true}
      bodyClassName={bodyClassName}
    />
  )
}
